import { handleFetchDivisions, showMessageError } from '@/shared/lib';
import { useSetState } from '@/shared/lib/customHooks';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { ReturnStatusTabProps } from './returnStatusTab.types';

/**
 * Supplies the study's return status tab with its loading state, division lists and navigation
 * actions. Fetches the Canadian and US state lists once on mount, combines the parent's loaded flag
 * with its own, and exposes handlers that open the create-return-shipping route or the linked study's
 * return status.
 *
 * @param props - The parent's isLoadedData flag and the study data.
 * @returns An object carrying the tab state, the combined loaded flag and the create-shipping and
 * view-now handlers.
 */
export const useReturnStatusTab = ({
  isLoadedData: isLoadedDataProp,
  data,
}: Pick<ReturnStatusTabProps, 'isLoadedData' | 'data'>) => {
  const navigate = useNavigate();

  const [state, setState] = useSetState<any>({
    isLoadedData: false,
    listStatesCA: [],
    listStatesUS: [],
  });

  const isLoadedData = isLoadedDataProp && state.isLoadedData;

  /**
   * Loads the state and province lists needed by the return-status address form. Fetches the Canadian and US divisions in parallel into state, logging and surfacing an error message on failure and marking the data loaded in a finally block.
   *
   * @returns A promise resolving once the division lists have been stored in state.
   */
  const fetchData = async () => {
    try {
      setState({ isLoadedData: false });
      const [listStatesCA, listStatesUS] = await Promise.all([
        handleFetchDivisions('CA'),
        handleFetchDivisions('US'),
      ]);
      setState({
        listStatesCA,
        listStatesUS,
      });
    } catch (error) {
      console.error('Failed to fetch divisions: ', error);
      showMessageError({ content: error instanceof Error ? error.message : undefined });
    } finally {
      setState({ isLoadedData: true });
    }
  };

  /**
   * Starts the return-shipping flow for a study. Navigates to the study's create-return-shipping route.
   *
   * @returns Nothing.
   */
  const onClickCreateShipping = () => {
    navigate(`/studies/${data?.id}/create-return-shipping`);
  };

  /**
   * Jumps to the linked parent study's return status. Replaces the current route with the first linked study and passes a flag asking that page to open its return-status tab.
   *
   * @returns Nothing.
   */
  const onClickViewNow = () => {
    navigate(`/studies/${data?.linkedStudies?.[0]?.id}`, {
      replace: true,
      state: {
        shouldNavigateToReturnStatusTab: true,
      },
    });
  };

  useEffect(() => {
    fetchData();
  }, []);

  return { state, isLoadedData, onClickCreateShipping, onClickViewNow };
};
